import { Table, Badge, ActionIcon, Group, Text, Box, Tooltip, Divider } from "@mantine/core";
import { IconEdit, IconTrash, IconToggleLeft, IconToggleRight, IconCopy, IconCode } from "@tabler/icons-react";
import { useCallback, memo } from "react";
import type { TemplateListItem } from "../../types/template";

/** Props for individual template row */
interface TemplateRowProperties {
  template: TemplateListItem;
  onEdit: (template: TemplateListItem) => void;
  onDelete: (template: TemplateListItem) => void;
  onToggleActive: (template: TemplateListItem) => void;
  onCopy?: (template: TemplateListItem) => void;
  onEditHtml?: (template: TemplateListItem) => void;
  isLoading: boolean;
}

/**
 * Individual row component to avoid inline functions in parent map()
 */
const TemplateRow = memo(function TemplateRow({
  template,
  onEdit,
  onDelete,
  onToggleActive,
  onCopy,
  onEditHtml,
  isLoading
}: TemplateRowProperties) {
  const handleEdit = useCallback(() => {
    onEdit(template);
  }, [onEdit, template]);

  const handleEditHtml = useCallback(() => {
    onEditHtml?.(template);
  }, [onEditHtml, template]);

  const handleCopy = useCallback(() => {
    onCopy?.(template);
  }, [onCopy, template]);

  const handleToggleActive = useCallback(() => {
    onToggleActive(template);
  }, [onToggleActive, template]);

  const handleDelete = useCallback(() => {
    onDelete(template);
  }, [onDelete, template]);

  return (
    <Table.Tr>
      {/* Status */}
      <Table.Td w={100}>
        <Badge color={template.ativo ? "green" : "gray"} variant="light" size="sm">
          {template.ativo ? "Ativo" : "Inativo"}
        </Badge>
      </Table.Td>

      {/* Nome - com mais peso visual */}
      <Table.Td w={280}>
        <Text fw={600} size="sm">
          {template.nome}
        </Text>
      </Table.Td>

      {/* Descrição */}
      <Table.Td>
        <Text size="sm" c="dimmed" lineClamp={2}>
          {template.descricao}
        </Text>
      </Table.Td>

      {/* Ações - Agrupadas por categoria */}
      <Table.Td w={220}>
        <Group gap="md" wrap="nowrap" justify="flex-end">
          {/* Grupo: Edição */}
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Editar template">
              <ActionIcon variant="subtle" color="blue" onClick={handleEdit} disabled={isLoading} size="sm">
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>

            {onEditHtml && (
              <Tooltip label="Editar HTML">
                <ActionIcon variant="subtle" color="cyan" onClick={handleEditHtml} disabled={isLoading} size="sm">
                  <IconCode size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          {/* Separador visual */}
          <Divider orientation="vertical" size="xs" />

          {/* Grupo: Operações */}
          <Group gap={4} wrap="nowrap">
            {onCopy && (
              <Tooltip label="Duplicar">
                <ActionIcon variant="subtle" color="violet" onClick={handleCopy} disabled={isLoading} size="sm">
                  <IconCopy size={18} />
                </ActionIcon>
              </Tooltip>
            )}

            <Tooltip label={template.ativo ? "Desativar" : "Ativar"}>
              <ActionIcon
                variant="subtle"
                color={template.ativo ? "orange" : "green"}
                onClick={handleToggleActive}
                disabled={isLoading}
                size="sm"
              >
                {template.ativo ? <IconToggleRight size={18} /> : <IconToggleLeft size={18} />}
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Separador visual */}
          <Divider orientation="vertical" size="xs" />

          {/* Grupo: Exclusão */}
          <Tooltip label="Apagar">
            <ActionIcon variant="subtle" color="red" onClick={handleDelete} disabled={isLoading} size="sm">
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
});

interface TemplateListProperties {
  /**
   * Array of templates to display
   */
  templates: TemplateListItem[];

  /**
   * Callback when edit button is clicked
   */
  onEdit: (template: TemplateListItem) => void;

  /**
   * Callback when delete button is clicked
   */
  onDelete: (template: TemplateListItem) => void;

  /**
   * Callback when toggle active button is clicked
   */
  onToggleActive: (template: TemplateListItem) => void;

  /**
   * Callback when "Use as Base" button is clicked
   */
  onCopy?: (template: TemplateListItem) => void;

  /**
   * Callback when "Edit HTML" button is clicked
   */
  onEditHtml?: (template: TemplateListItem) => void;

  /**
   * Whether actions are disabled (e.g., during loading)
   */
  isLoading?: boolean;
}

/**
 * Table component for displaying templates with actions
 *
 * Features:
 * - Displays template metadata (name, description, status)
 * - Action buttons for edit, toggle active, and delete
 * - Visual status indicators (badges)
 * - Loading states
 * @param root0
 * @param root0.templates
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.onToggleActive
 * @param root0.isLoading
 * @param root0.onCopy
 * @param root0.onEditHtml
 * @example
 * ```tsx
 * <TemplateList
 *   templates={templates}
 *   onEdit={(template) => openEditModal(template)}
 *   onDelete={(template) => openDeleteConfirm(template)}
 *   onToggleActive={(template) => toggleActive(template)}
 * />
 * ```
 */
export function TemplateList({
  templates,
  onEdit,
  onDelete,
  onToggleActive,
  onCopy,
  onEditHtml,
  isLoading = false
}: TemplateListProperties) {
  if (templates.length === 0) {
    return (
      <Box p="xl" style={{ textAlign: "center" }}>
        <Text c="dimmed" size="sm">
          Nenhum template encontrado.
        </Text>
        <Text c="dimmed" size="xs" mt="xs">
          Crie um novo template para começar.
        </Text>
      </Box>
    );
  }

  const rows = templates.map((template) => (
    <TemplateRow
      key={template.id}
      template={template}
      onEdit={onEdit}
      onDelete={onDelete}
      onToggleActive={onToggleActive}
      onCopy={onCopy}
      onEditHtml={onEditHtml}
      isLoading={isLoading}
    />
  ));

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={100}>Status</Table.Th>
          <Table.Th w={280}>Nome</Table.Th>
          <Table.Th>Descrição</Table.Th>
          <Table.Th w={220} ta="right">
            Ações
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  );
}
